"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.inferCrmCustomPropertyType = exports.isCrmCustomPropertyImportValueValid = exports.parseCrmCustomPropertyImportValue = exports.isCrmPropertyImportFieldKey = exports.buildCrmPropertyImportFieldKey = exports.CRM_MULTI_SELECT_IMPORT_SEPARATOR = exports.CRM_PROPERTY_IMPORT_FIELD_KEY_PREFIX = void 0;
const validator_1 = require("validator");
const crm_custom_property_1 = require("../enums/crm-custom-property");
const dates_1 = require("../internationalization/features/dates");
const country_1 = require("../internationalization/enums/country");
const crm_date_value_1 = require("./crm-date-value");
const import_amount_1 = require("./import.amount");
const phone_1 = require("./phone");
exports.CRM_PROPERTY_IMPORT_FIELD_KEY_PREFIX = "crm-property-";
exports.CRM_MULTI_SELECT_IMPORT_SEPARATOR = /[;,]/;
const CRM_PROPERTY_IMPORT_FIELD_KEY_REGEX = /^crm-property-\d+$/;
const NUMBER_INPUT_REGEX = /^-?\d+(?:[.,]\d+)*$/;
const TRUE_WORDS = ["yes", "true", "y", "oui", "si", "sí", "ja"];
const FALSE_WORDS = ["no", "false", "n", "non", "nein"];
const INFERENCE_SAMPLE_BOOLEAN_TOKENS = new Set([...TRUE_WORDS, ...FALSE_WORDS]);
const TRUE_TOKENS = new Set([...TRUE_WORDS, "1"]);
const FALSE_TOKENS = new Set([...FALSE_WORDS, "0"]);
const buildCrmPropertyImportFieldKey = (index) => `${exports.CRM_PROPERTY_IMPORT_FIELD_KEY_PREFIX}${index}`;
exports.buildCrmPropertyImportFieldKey = buildCrmPropertyImportFieldKey;
const isCrmPropertyImportFieldKey = (key) => CRM_PROPERTY_IMPORT_FIELD_KEY_REGEX.test(key);
exports.isCrmPropertyImportFieldKey = isCrmPropertyImportFieldKey;
const toPhoneNumber = (raw, country) => (0, phone_1.normalizePhoneToE164)(raw, country ? country_1.ORGANIZATION_COUNTRY_MAP[country].countryCode : null);
const toIsoDateFromCountryFormat = (raw, country) => {
    const parsed = (0, dates_1.parseDateByCountry)(raw, country);
    if (Number.isNaN(parsed.getTime())) {
        return null;
    }
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const day = String(parsed.getDate()).padStart(2, "0");
    return (0, crm_date_value_1.normalizeCrmDateValue)(`${parsed.getFullYear()}-${month}-${day}`);
};
const parseDate = (raw, country) => (0, crm_date_value_1.normalizeCrmDateValue)(raw) ?? toIsoDateFromCountryFormat(raw, country);
const parseNumber = (raw) => {
    if (!NUMBER_INPUT_REGEX.test(raw)) {
        return null;
    }
    const parsed = (0, import_amount_1.parseAmountWithCommaOrThousands)(raw);
    return Number.isFinite(parsed) ? parsed : null;
};
const parseBoolean = (raw) => {
    const token = raw.toLowerCase();
    if (TRUE_TOKENS.has(token)) {
        return true;
    }
    return FALSE_TOKENS.has(token) ? false : null;
};
const parseMultiSelect = (raw) => Array.from(new Set(raw
    .split(exports.CRM_MULTI_SELECT_IMPORT_SEPARATOR)
    .map((choice) => choice.trim())
    .filter((choice) => choice.length > 0)));
const parseCrmCustomPropertyImportValue = ({ raw, propertyType, country, }) => {
    const trimmed = raw.trim();
    if (trimmed === "") {
        return null;
    }
    switch (propertyType) {
        case crm_custom_property_1.CrmCustomPropertyType.Text:
        case crm_custom_property_1.CrmCustomPropertyType.SingleSelect:
            return trimmed;
        case crm_custom_property_1.CrmCustomPropertyType.Number:
            return parseNumber(trimmed);
        case crm_custom_property_1.CrmCustomPropertyType.Phone:
            return toPhoneNumber(trimmed, country);
        case crm_custom_property_1.CrmCustomPropertyType.Email:
            return (0, validator_1.isEmail)(trimmed) ? trimmed.toLowerCase() : null;
        case crm_custom_property_1.CrmCustomPropertyType.Date:
            return parseDate(trimmed, country);
        case crm_custom_property_1.CrmCustomPropertyType.Boolean:
            return parseBoolean(trimmed);
        case crm_custom_property_1.CrmCustomPropertyType.MultiSelect: {
            const choices = parseMultiSelect(trimmed);
            return choices.length > 0 ? choices : null;
        }
        default:
            return null;
    }
};
exports.parseCrmCustomPropertyImportValue = parseCrmCustomPropertyImportValue;
const isCrmCustomPropertyImportValueValid = ({ raw, propertyType, country, }) => raw.trim() === "" || (0, exports.parseCrmCustomPropertyImportValue)({ raw, propertyType, country }) !== null;
exports.isCrmCustomPropertyImportValueValid = isCrmCustomPropertyImportValueValid;
const inferCrmCustomPropertyType = ({ samples, country, }) => {
    const values = samples.map((sample) => sample.trim()).filter((sample) => sample !== "");
    if (values.length === 0) {
        return crm_custom_property_1.CrmCustomPropertyType.Text;
    }
    const distinctValues = Array.from(new Set(values));
    if (distinctValues.every((value) => (0, validator_1.isEmail)(value))) {
        return crm_custom_property_1.CrmCustomPropertyType.Email;
    }
    if (distinctValues.every((value) => parseDate(value, country) !== null)) {
        return crm_custom_property_1.CrmCustomPropertyType.Date;
    }
    if (distinctValues.every((value) => toPhoneNumber(value, country) !== null)) {
        return crm_custom_property_1.CrmCustomPropertyType.Phone;
    }
    if (distinctValues.every((value) => INFERENCE_SAMPLE_BOOLEAN_TOKENS.has(value.toLowerCase()))) {
        return crm_custom_property_1.CrmCustomPropertyType.Boolean;
    }
    if (distinctValues.every((value) => parseNumber(value) !== null)) {
        return crm_custom_property_1.CrmCustomPropertyType.Number;
    }
    if (distinctValues.length === values.length) {
        return crm_custom_property_1.CrmCustomPropertyType.Text;
    }
    if (distinctValues.some((value) => exports.CRM_MULTI_SELECT_IMPORT_SEPARATOR.test(value))) {
        return crm_custom_property_1.CrmCustomPropertyType.MultiSelect;
    }
    return crm_custom_property_1.CrmCustomPropertyType.SingleSelect;
};
exports.inferCrmCustomPropertyType = inferCrmCustomPropertyType;
